<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { Chart, registerables } from 'chart.js'
import type { Chart as ChartJsInstance } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { storeToRefs } from 'pinia'
import { useI18n } from '@/composable/useI18n'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { getWaterfallBarColor } from '@/utils/reportOpportunities'

const { t } = useI18n()

Chart.register(...registerables, ChartDataLabels)

const store = useFareOptimizerStore()
const { waterfallChartBars, waterfallPeakWasteBar, waterfallFreeMarketBar, tariffTotalTable, waterfallSubtotal } =
  storeToRefs(store)

const canvasRef = ref<HTMLCanvasElement | null>(null)
const chartContainerRef = ref<HTMLElement | null>(null)
const isLoadingOpportunityCalc = computed(() => store.isLoadingOpportunity)
const chartInstance = shallowRef<ChartJsInstance | null>(null)

let resizeObserver: ResizeObserver | null = null
let resizeTimeout: ReturnType<typeof setTimeout> | null = null
let chartUpdateTimeout: ReturnType<typeof setTimeout> | null = null

const GREY_BAR = 'rgba(128, 128, 128, 0.5)'
const BLUE_BAR = 'rgba(30, 144, 255, 0.5)'

/** Quebra rótulos longos do eixo X em até 2 linhas, sem rotação diagonal. */
function wrapChartAxisLabel(label: string): string | string[] {
  if (label.length <= 14) return label

  const parenMatch = label.match(/^(.+?)\s*(\(.+\))$/)
  if (parenMatch) {
    return [parenMatch[1].trim(), parenMatch[2].trim()]
  }

  const words = label.split(/\s+/)
  if (words.length <= 1) return label

  const mid = Math.ceil(words.length / 2)
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')]
}

function countAxisLabelLines(label: string): number {
  const wrapped = wrapChartAxisLabel(label)
  return Array.isArray(wrapped) ? wrapped.length : 1
}

const middleBars = computed(() => {
  const bars = [...waterfallChartBars.value]
  if (waterfallPeakWasteBar.value) {
    bars.push({
      ...waterfallPeakWasteBar.value,
      label: t('contract_optimizer.opportunity_calc.peak_waste_potential_savings'),
    })
  }
  if (waterfallFreeMarketBar.value) {
    bars.push(waterfallFreeMarketBar.value)
  }
  return bars
})

const chartValues = computed(() => {
  const totalAmount = tariffTotalTable.value || 0
  const subtotal = waterfallSubtotal.value
  const subtotalLabel = t('contract_optimizer.opportunity_calc.subtotal')

  const items = [
    ...(totalAmount > 0
      ? [{ label: t('contract_optimizer.opportunity_calc.current_value'), value: totalAmount, color: GREY_BAR }]
      : []),
    ...middleBars.value
      .filter((bar) => bar.value > 0)
      .map((bar, index) => ({
        label: bar.label,
        value: bar.value,
        color: getWaterfallBarColor(bar.key, index),
      })),
  ]

  if (totalAmount > 0) {
    items.push({ label: subtotalLabel, value: subtotal, color: BLUE_BAR })
  }

  return items
})

const chartData = computed(() => ({
  labels: chartValues.value.map((item) => item.label),
  datasets: [
    {
      label: 'Valor (R$)',
      data: chartValues.value.map((item) => item.value),
      backgroundColor: chartValues.value.map((item) => item.color),
      borderColor: chartValues.value.map((item) => item.color.replace('0.5', '1')),
      borderWidth: 1,
    },
  ],
}))

const barCount = computed(() => chartValues.value.length)
const xAxisLabelLineCount = computed(() =>
  Math.max(1, ...chartValues.value.map((item) => countAxisLabelLines(item.label))),
)
const canvasHeight = computed(() => {
  const base = Math.max(280, Math.min(520, barCount.value * 56))
  const extraForLabels = (xAxisLabelLineCount.value - 1) * 24
  return base + extraForLabels
})
const hasChartData = computed(() => chartValues.value.length > 0)

const chartLayoutOptions = computed(() => {
  const count = barCount.value
  const categoryPercentage = count <= 3 ? 0.85 : count <= 5 ? 0.7 : 0.55
  const barPercentage = count <= 3 ? 0.9 : count <= 5 ? 0.75 : 0.6
  const datalabelSize = count > 5 ? 9 : 11

  return { categoryPercentage, barPercentage, datalabelSize }
})

const waitForLayout = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })

const destroyChart = () => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
    chartInstance.value = null
  }
}

const buildChartOptions = () => {
  const { categoryPercentage, barPercentage, datalabelSize } = chartLayoutOptions.value
  const labels = chartValues.value.map((item) => item.label)
  const bottomPadding = 12 + (xAxisLabelLineCount.value - 1) * 14

  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30,
        bottom: bottomPadding,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: string | number) => `${useCurrencyFormaterFromCents(Number(value))}`,
        },
      },
      x: {
        stacked: true,
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: barCount.value > 4 ? 10 : 11,
          },
          callback: (_value: string | number, index: number) =>
            wrapChartAxisLabel(labels[index] ?? ''),
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: { raw: unknown }) => useCurrencyFormaterFromCents(Number(tooltipItem.raw)),
        },
      },
      datalabels: {
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 4,
        color: '#000',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        borderRadius: 3,
        padding: {
          top: 2,
          bottom: 2,
          left: 4,
          right: 4,
        },
        font: {
          size: datalabelSize,
          weight: 'bold' as const,
        },
        formatter: (value: number) => useCurrencyFormaterFromCents(Number(value)),
      },
    },
    datasets: {
      bar: {
        categoryPercentage,
        barPercentage,
      },
    },
  }
}

const initializeChart = async () => {
  await nextTick()
  await waitForLayout()

  const ctx = canvasRef.value
  if (!ctx || isLoadingOpportunityCalc.value || !hasChartData.value) return

  destroyChart()

  try {
    chartInstance.value = new Chart(ctx, {
      type: 'bar',
      data: chartData.value,
      options: buildChartOptions(),
    })
    chartInstance.value.resize()
  } catch {
    // Erro ao inicializar gráfico
  }
}

const updateChart = async () => {
  await nextTick()
  await waitForLayout()

  const ctx = canvasRef.value
  if (!ctx || isLoadingOpportunityCalc.value || !hasChartData.value) return

  if (chartInstance.value) {
    chartInstance.value.data = chartData.value
    chartInstance.value.options = buildChartOptions()
    chartInstance.value.update()
    chartInstance.value.resize()
    return
  }

  await initializeChart()
}

const scheduleChartUpdate = () => {
  if (chartUpdateTimeout) clearTimeout(chartUpdateTimeout)
  chartUpdateTimeout = setTimeout(() => {
    if (isLoadingOpportunityCalc.value || !hasChartData.value) {
      destroyChart()
      return
    }
    void updateChart()
  }, 0)
}

const handleContainerResize = () => {
  if (resizeTimeout) clearTimeout(resizeTimeout)
  resizeTimeout = setTimeout(() => {
    chartInstance.value?.resize()
  }, 25)
}

const setupResizeObserver = () => {
  if (!chartContainerRef.value || resizeObserver) return

  resizeObserver = new ResizeObserver(handleContainerResize)
  resizeObserver.observe(chartContainerRef.value)
}

watch(isLoadingOpportunityCalc, (isLoading) => {
  if (isLoading) {
    destroyChart()
    return
  }
  scheduleChartUpdate()
})

watch(
  () => [
    waterfallChartBars.value,
    waterfallPeakWasteBar.value,
    waterfallFreeMarketBar.value,
    tariffTotalTable.value,
    waterfallSubtotal.value,
  ],
  () => {
    if (!isLoadingOpportunityCalc.value) {
      scheduleChartUpdate()
    }
  },
  { deep: true },
)

watch(hasChartData, (hasData) => {
  if (!hasData) {
    destroyChart()
  }
})

onMounted(() => {
  nextTick(() => {
    setupResizeObserver()
    if (!isLoadingOpportunityCalc.value && hasChartData.value) {
      scheduleChartUpdate()
    }
  })
})

onBeforeUnmount(() => {
  if (chartUpdateTimeout) clearTimeout(chartUpdateTimeout)
  if (resizeTimeout) clearTimeout(resizeTimeout)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  destroyChart()
})
</script>

<template>
  <div class="flex h-full flex-col bg-white shadow-4">
    <div class="flex items-center justify-between border-b-2 border-stroke px-6 py-4">
      <h1 class="border-stroke font-satoshi text-base font-bold text-black">
        {{ t('contract_optimizer.opportunity_calc.name') }}
      </h1>
    </div>

    <div class="relative flex min-h-100 flex-1 w-full flex-col justify-center p-6">
      <div
        v-if="isLoadingOpportunityCalc"
        class="absolute inset-0 z-10 flex items-center justify-center bg-white p-6"
      >
        <p class="text-center">{{ t('contract_optimizer.opportunity_calc.loading') }}</p>
      </div>

      <div v-if="!hasChartData && !isLoadingOpportunityCalc" class="flex min-h-100 flex-1 w-full items-center justify-center">
        <p class="text-center">{{ t('contract_optimizer.opportunity_calc.empty') }}</p>
      </div>

      <div
        v-show="hasChartData"
        ref="chartContainerRef"
        class="relative w-full min-w-0"
        :style="{ height: `${canvasHeight}px` }"
      >
        <canvas ref="canvasRef" class="h-full w-full" />
      </div>
    </div>
  </div>
</template>
